import "./AddInputModal.scss";
import "../../Components/FormCheck.scss";

import * as R from "ramda";

import { Button, ButtonType, Spinner } from "../../Components";
import {
  DEFAULT_MAX_YEAR,
  DEFAULT_MIN_YEAR,
  FOCUS_OPTIONS,
} from "../../Components/DatePicker/DatePickerButton/DatePickerConstants";
import { Form, Modal } from "react-bootstrap";
import { MenuItem, TimelineEntry } from "@blisspointmedia/bpm-types/dist/OfflineInputs";
import React, { useCallback, useMemo, useState } from "react";
import { isAfter, isBefore, isEqual } from "date-fns";

import { DPCalendar } from "../../Components/DatePicker/DPCalendar";
import DateInputField from "../../Components/DatePicker/DateInputField/DateInputField";
import { DateRange } from "../../utils/types";
import EditableDropDown from "../../Components/EditableDropDown";
import { NumericFormat } from "react-number-format";
import RenameModal from "../../SingleChannel/RenameModal";
import { formatInTimeZone } from "date-fns-tz";
import { invalidPromoDates } from "./OfflineInputsUtils";
import { useSetAreYouSure } from "../../redux/modals";

interface AddPromoModalProps {
  show: boolean;
  handleClose: () => void;
  stageEntry: (input: TimelineEntry) => void;
  promos: MenuItem[];
  setPromos: (promos: MenuItem[]) => void;
  offers: MenuItem[];
  setOffers: (offers: MenuItem[]) => void;
  userFullName: string;
}

const disabledDeleteTooltipText = (field: string) =>
  `${field} already committed to the backend and can’t be deleted. Contact us if it needs to be removed.`;

const AddPromoModal: React.FC<AddPromoModalProps> = ({
  show,
  handleClose,
  stageEntry,
  promos,
  setPromos,
  offers,
  setOffers,
  userFullName,
}) => {
  const [focus, setFocus] = useState<typeof FOCUS_OPTIONS[number]>(FOCUS_OPTIONS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState<MenuItem | undefined>();
  const [percentageDiscountChecked, setPercentageDiscountChecked] = useState<boolean>(false);
  const [percentageDiscount, setPercentageDiscount] = useState<number | null>(null);
  const [specialOfferChecked, setSpecialOfferChecked] = useState<boolean>(false);
  const [selectedOffer, setSelectedOffer] = useState<MenuItem | undefined>();
  const [dateRange, setDateRange] = useState<Partial<DateRange>>();
  const [showAddOfferModal, setShowAddOfferModal] = useState<boolean>(false);
  const [showAddPromoModal, setShowAddPromoModal] = useState<boolean>(false);
  const setAreYouSure = useSetAreYouSure(true);

  const today = formatInTimeZone(new Date(), "America/New_York", "yyyy-MM-dd");

  const resetState = useCallback(() => {
    setIsSubmitting(false);
    setPercentageDiscountChecked(false);
    setPercentageDiscount(null);
    setSpecialOfferChecked(false);
    setDateRange(undefined);
    setShowAddOfferModal(false);
    setShowAddPromoModal(false);
    setSelectedOffer(undefined);
    setSelectedPromo(undefined);
  }, []);

  const isSubmitDisabled = useMemo(() => {
    return (
      isSubmitting ||
      !selectedPromo ||
      (!percentageDiscount && !selectedOffer) ||
      !dateRange?.start ||
      !dateRange?.end ||
      isAfter(new Date(dateRange.start), new Date(dateRange.end))
    );
  }, [
    isSubmitting,
    selectedPromo,
    percentageDiscount,
    selectedOffer,
    dateRange?.start,
    dateRange?.end,
  ]);

  const onCalendarChange = useCallback(
    (dateRange: Partial<DateRange>, focus: typeof FOCUS_OPTIONS[number]) => {
      setDateRange(dateRange);
      setFocus(focus);
    },
    []
  );

  const addModal = (
    field: string,
    showModal: boolean,
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>,
    itemArr: MenuItem[],
    setItemArr: (item: MenuItem[]) => void,
    setSelectedItem: React.Dispatch<React.SetStateAction<MenuItem | undefined>>,
    setSelectedItemChecked?: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    return (
      showModal && (
        <RenameModal
          applyOnClick={label => {
            addDropdownItem(
              label,
              field,
              itemArr,
              setItemArr,
              setSelectedItem,
              setShowModal,
              setSelectedItemChecked
            );
          }}
          applyText="Save"
          cancelText="Discard"
          onCancel={() => {
            setShowModal(false);
          }}
          onHide={() => setShowModal(false)}
          placeholder={`New ${field} Name`}
          subTitle={`New ${field} Name`}
          title={`Add New ${field}`}
        />
      )
    );
  };

  const addPromoModal = addModal(
    "Promo",
    showAddPromoModal,
    setShowAddPromoModal,
    promos,
    setPromos,
    setSelectedPromo
  );

  const addOfferModal = addModal(
    "Offer",
    showAddOfferModal,
    setShowAddOfferModal,
    offers,
    setOffers,
    setSelectedOffer,
    setSpecialOfferChecked
  );

  const addDropdownItem = useCallback(
    (
      label: string,
      field: string,
      itemArr: MenuItem[],
      setItemArr: (item: MenuItem[]) => void,
      setSelectedItem: (value: React.SetStateAction<MenuItem | undefined>) => void,
      setShowModal: React.Dispatch<React.SetStateAction<boolean>>,
      setSelectedItemChecked?: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
      const newItem = {
        id: -Date.now(),
        label: label,
        updatedAt: today,
        updatedBy: userFullName,
        isDeletable: true,
      };
      const addItem = () => {
        setItemArr([...itemArr, newItem]);
        setSelectedItem(newItem);
        if (setSelectedItemChecked) {
          setSelectedItemChecked(true);
        }
        setShowModal(false);
      };
      if (itemArr.some(entry => entry.label === label)) {
        setShowModal(false);
        setAreYouSure({
          title: `${label} ${field} already exists`,
          message: `Adding duplicate ${field}s can break our modeling. Please consider using the existing entry or a different name.`,
          variant: "warning",
          cancelText: "Cancel",
          okayText: "Okay",
          promise: false,
          onOkay: addItem,
        });
        return;
      }
      addItem();
    },
    [setAreYouSure, today, userFullName]
  );

  const deleteItem = useCallback(
    (
      item: MenuItem,
      itemArr: MenuItem[],
      setItemArr: (item: MenuItem[]) => void,
      setSelectedItem: (value: React.SetStateAction<MenuItem | undefined>) => void,
      field: string
    ) => {
      setAreYouSure({
        title: `Delete ${field}`,
        message: `You are about to delete this ${field}, are you sure?`,
        variant: "warning",
        cancelText: "Never mind",
        okayText: "Yes",
        promise: false,
        onOkay: () => {
          if (item) {
            setItemArr(itemArr.filter(ele => ele.id !== item.id));
            setSelectedItem(undefined);
          }
        },
      });
    },
    [setAreYouSure]
  );
  return (
    <Modal
      className="addInputModal addPromoModal"
      show={show}
      onHide={() => {
        handleClose();
        resetState();
      }}
      size="xl"
      centered
    >
      {/* Modals */}
      {addPromoModal}
      {addOfferModal}
      <Modal.Header>
        <h4 className="modalTitle">Add Promo</h4>
        <div className="subTitle">*Required fields marked with an asterisk.</div>
      </Modal.Header>
      <Modal.Body>
        <div className="promoModalColumn" id="col1">
          <div className="promoNameInput">
            <div className="sectionTitle">
              Promo Name<span className="asterisk">*</span>
            </div>
            <EditableDropDown
              className="promoDropdown"
              design="secondary"
              menuItems={promos}
              addItemText="Add Promo"
              dropdownLabel={"Select Promo"}
              deleteItemOnClick={(item: MenuItem) => {
                setSelectedPromo(item);
                deleteItem(item, promos, setPromos, setSelectedPromo, "Promo");
              }}
              addItemOnClick={() => setShowAddPromoModal(true)}
              selectedItem={selectedPromo}
              setSelectedItem={setSelectedPromo}
              disabledDeleteTooltipText={disabledDeleteTooltipText("Promo")}
            />
            <div className="inputExplanation">
              <p>
                Important: Please use consistent promo names to ensure accurate tracking and
                reporting.
              </p>
            </div>
          </div>
        </div>
        <div className="promoModalColumn" id="col2">
          <div className="promoTypeInput">
            <div className="sectionTitle">
              Promotion Type<span className="asterisk">*</span>
            </div>
            <div className="percentage">
              <div className="checkbox">
                <Form.Check
                  type="checkbox"
                  label="Percentage"
                  checked={percentageDiscountChecked}
                  onChange={e => {
                    setPercentageDiscountChecked(!percentageDiscountChecked);
                    if (!e.target.checked) {
                      setPercentageDiscount(null);
                    }
                  }}
                />
              </div>
              <div className="inputAndExplanation">
                <NumericFormat
                  className="form-control"
                  displayType="input"
                  placeholder="ex: 55%"
                  suffix="%"
                  allowNegative={false}
                  isAllowed={values => {
                    const { floatValue } = values;
                    return floatValue === undefined || (floatValue >= 0 && floatValue <= 100);
                  }}
                  value={
                    percentageDiscount !== null &&
                    percentageDiscount > 0 &&
                    percentageDiscount <= 100
                      ? percentageDiscount
                      : ""
                  }
                  onValueChange={values => {
                    const { floatValue } = values;
                    const enteredPercentage = floatValue === undefined ? null : floatValue;
                    setPercentageDiscount(enteredPercentage);
                    enteredPercentage
                      ? setPercentageDiscountChecked(true)
                      : setPercentageDiscountChecked(false);
                  }}
                />
                <div className="inputExplanation">
                  <p>Must be less than or equal to 100%.</p>
                  <p>
                    Important: If multiple percentage off offers occur during the same period,
                    please enter each as a separate promo.
                  </p>
                </div>
              </div>
            </div>
            <div className="specialOffer">
              <div className="checkbox">
                <Form.Check
                  type="checkbox"
                  label="Special Offer"
                  checked={specialOfferChecked}
                  onChange={e => {
                    setSpecialOfferChecked(!specialOfferChecked);
                    if (!e.target.checked) {
                      setSelectedOffer(undefined);
                    }
                  }}
                />
              </div>
              <div className="inputAndExplanation">
                <EditableDropDown
                  design="secondary"
                  menuItems={offers}
                  addItemText="Add Offer"
                  dropdownLabel="Select Offer"
                  deleteItemOnClick={(item: MenuItem) => {
                    setSelectedOffer(item);
                    deleteItem(item, offers, setOffers, setSelectedOffer, "Offer");
                  }}
                  addItemOnClick={() => setShowAddOfferModal(true)}
                  selectedItem={selectedOffer}
                  setSelectedItem={(offer: MenuItem | undefined) => {
                    setSelectedOffer(offer);
                    setSpecialOfferChecked(Boolean(offer));
                  }}
                  disabledDeleteTooltipText={disabledDeleteTooltipText("Offer")}
                />
                <div className="inputExplanation">
                  <p>
                    Important: If multiple special offers occur during the same period, please enter
                    each as a separate promo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="promoModalColumn" id="col3">
          <div className="timeFrame">
            <div className="sectionTitle">
              Time Frame<span className="asterisk">*</span>
            </div>
            <div className="topContainer">
              <DateInputField
                label="Start Date"
                value={dateRange?.start || ""}
                setValue={newStartDate => {
                  setDateRange(prev => ({
                    ...prev,
                    start: newStartDate,
                  }));
                }}
                invalidateDateOrRange={invalidPromoDates}
                customValidation={[
                  {
                    validate: value => {
                      const parsedDate = new Date(value);
                      if (!dateRange?.end) {
                        return true;
                      } else {
                        return (
                          isBefore(parsedDate, new Date(dateRange.end)) ||
                          isEqual(parsedDate, new Date(dateRange.end))
                        );
                      }
                    },
                    errorMessage: "Start date must be before end date.",
                  },
                ]}
              />
              <DateInputField
                label="End Date"
                value={dateRange?.end || ""}
                setValue={newEndDate => {
                  setDateRange(prev => ({
                    ...prev,
                    end: newEndDate,
                  }));
                }}
                invalidateDateOrRange={invalidPromoDates}
                customValidation={[
                  {
                    validate: value => {
                      const parsedDate = new Date(value);
                      if (!dateRange?.start) {
                        return true;
                      } else {
                        return (
                          isAfter(parsedDate, new Date(dateRange.start)) ||
                          isEqual(parsedDate, new Date(dateRange.start))
                        );
                      }
                    },
                    errorMessage: "",
                  },
                ]}
              />
            </div>
            <div className="bottomContainer">
              <DPCalendar
                start={dateRange?.start}
                end={dateRange?.end}
                focus={focus}
                onChange={onCalendarChange}
                isOutsideRange={date => false}
                isDayBlocked={date => false}
                years={R.range(DEFAULT_MIN_YEAR, DEFAULT_MAX_YEAR + 1)}
                currentMonthSecond={false}
                numVisibleMonths={2}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type={ButtonType.FILLED}
          design="secondary"
          onClick={() => {
            handleClose();
            resetState();
          }}
        >
          Cancel
        </Button>
        <Button
          type={ButtonType.FILLED}
          onClick={async () => {
            if (
              !dateRange?.start ||
              !dateRange.end ||
              !selectedPromo ||
              (!selectedOffer && !percentageDiscount) ||
              isSubmitDisabled
            ) {
              return;
            }
            setIsSubmitting(true);
            await stageEntry({
              promoName: selectedPromo.label,
              percentageDiscount: percentageDiscount,
              specialOfferName: selectedOffer?.label || null,
              startDate: dateRange.start,
              endDate: dateRange.end,
            });
            handleClose();
            resetState();
          }}
          disabled={isSubmitDisabled}
        >
          {isSubmitting ? <Spinner /> : "Apply"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddPromoModal;
